"use client";

import { useEffect } from "react";
import Lenis from "lenis";
import { usePathname } from "next/navigation";

export default function useLenis() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Disable smooth-scrolling interceptor on admin routes so native scrolling works 100% reliably
    if (pathname?.startsWith("/admin")) return;

    // Honor user motion preference
    const reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reduceMotionQuery.matches) return;

    // Never hijack native touch scrolling on mobile or tablet devices
    const isTouchDevice =
      window.matchMedia("(pointer: coarse)").matches ||
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    // On low-core or low-memory devices, let the browser's hardware-accelerated native scroll run without overhead
    const cores = navigator.hardwareConcurrency ?? 8;
    const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
    if (cores <= 4 || memory <= 4) return;

    // Configure snappy, immediate, responsive smooth scrolling (zero floaty lag)
    const lenis = new Lenis({
      duration: 0.65,
      lerp: 0.18,
      smoothWheel: true,
      syncTouch: false,
      wheelMultiplier: 1.0,
      touchMultiplier: 1.0,
      allowNestedScroll: true,
      prevent: (node) => {
        return Boolean(
          node.hasAttribute("data-lenis-prevent") ||
            node.closest?.("[data-lenis-prevent]") ||
            node.closest?.(".modal-scroll-area")
        );
      },
    });

    (window as any).__lenis = lenis;

    let rafId = 0;
    let isRunning = true;

    const raf = (time: number) => {
      if (!isRunning) return;
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };

    rafId = requestAnimationFrame(raf);

    return () => {
      isRunning = false;
      cancelAnimationFrame(rafId);
      lenis.destroy();
      delete (window as any).__lenis;
    };
  }, [pathname]);
}
